import 'reflect-metadata';
import { ISettings } from './SettingsService';
import { ILogger } from './LoggerService';
import { injectable, inject } from 'inversify-props';

/** Responsible for storing data to local storage in the browser. */
export interface IStorage {

	/**
	 * Stores a value to local storage using the the specified key.
	 * @param key The name of the key to add to local storage.
	 * @param value The object to add to local storage.
	 */
	store(key: string, value: any): void;

	/**
	 * Retrieves a value from local storage with the specified key.
	 * @param key The key to retrieve from local storage.
	 */
	retrieve(key: string): string;

	/**
	 * Removes the specified key from local storage.
	 * @param key The key to remove from local storage.
	 */
	remove(key: string): void;

}

/** Responsible for storing data to local storage in the browser. */
@injectable()
export class StorageService implements IStorage {

	/**
	 * Creates an instance of the StorageService class
	 */
	constructor(
		@inject('ISettings') private settings: ISettings,
		@inject('ILogger') private logger: ILogger) { }

	/**
	 * Stores a value to local storage using the the specified key.
	 * @param key The name of the key to add to local storage.
	 * @param value The object to add to local storage.
	 */
	public store(key: string, value: any) {
		key = `${this.settings.storagePrefix}_${key}`;
		value = JSON.stringify(value);
		localStorage.setItem(key, value);
		this.logger.log(`[STORAGE] Stored object with the key ${key} to local storage`);
	}

	/**
	 * Retrieves a value from local storage with the specified key.
	 * @param key The key to retrieve from local storage.
	 */
	public retrieve(key: string): string {
		key = `${this.settings.storagePrefix}_${key}`;
		const json = localStorage.getItem(key);
		this.logger.log(`[STORAGE] Retrieved object with the key ${key} from local storage`);
		return json;
	}

	/**
	 * Removes the specified key from local storage.
	 * @param key The key to remove from local storage.
	 */
	public remove(key: string): void {
		key = `${this.settings.storagePrefix}_${key}`;
		const json = localStorage.removeItem(key);
		this.logger.log(`[STORAGE] Removed object with the key ${key} from local storage`);
	}

}
